import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiPost } from '../utils/api';

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: '' });

  // Sanitize input to prevent XSS
  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return input;
    return input.replace(/[<>]/g, '').trim();
  };

  // Check password strength
  const checkPasswordStrength = (password) => {
    let score = 0;
    let feedback = [];

    if (password.length >= 8) score += 1;
    else feedback.push('At least 8 characters');

    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('Include lowercase letter');

    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('Include uppercase letter');

    if (/[0-9]/.test(password)) score += 1;
    else feedback.push('Include number');

    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    else feedback.push('Include special character');

    return {
      score,
      feedback: feedback.length > 0 ? feedback.join(', ') : 'Strong password!'
    };
  };

  const handlePasswordChange = (e) => {
    const newPassword = sanitizeInput(e.target.value);
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
    setMessage(''); // Clear previous messages
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    // Enhanced validation
    if (!password || password.length < 8) {
      setMessage('Password must be at least 8 characters long.');
      return;
    }
    
    // Disallow very common passwords
    const commonPw = ['password','123456','123456789','qwerty','111111','123123','abc123'];
    if (commonPw.includes(password.toLowerCase())) {
      setMessage('Please choose a stronger password.');
      return;
    }

    if (passwordStrength.score < 3) {
      setMessage('Password is too weak. Please include uppercase, lowercase, numbers, and special characters.');
      return;
    }
    
    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    // Validate reset token format
    const isValidToken = typeof token === 'string' && /^[A-Za-z0-9._-]{10,2048}$/.test(token);
    if (!isValidToken) {
      safeReportError('invalid_reset_token');
      setMessage('Invalid or missing reset token.');
      return;
    }

    setLoading(true);
    try {
      const res = await apiPost('/api/auth/reset-password', { token, password });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to reset password');
      setSuccess(true);
      setMessage('Password reset successful! You can now log in.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      safeReportError('reset_password_failed');
      setMessage(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (score) => {
    if (score < 2) return 'text-red-500';
    if (score < 4) return 'text-yellow-500';
    return 'text-green-500';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-100">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full flex flex-col items-center">
        <h2 className="text-2xl font-bold mb-2 text-blue-700">Reset Your Password</h2>
        <p className="text-gray-500 mb-6 text-center">Enter your new password below.</p>
        <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit}>
          <div>
            <input
              type="password"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base"
              placeholder="New password"
              value={password}
              onChange={handlePasswordChange}
              disabled={loading || success}
              required
            />
            {password && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`h-1 flex-1 rounded ${
                        passwordStrength.score >= level ? getStrengthColor(passwordStrength.score) : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className={`text-xs ${getStrengthColor(passwordStrength.score)}`}>
                  {passwordStrength.feedback}
                </p>
              </div>
            )}
          </div>
          <input
            type="password"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={e => {
              setConfirmPassword(sanitizeInput(e.target.value));
              setMessage('');
            }}
            disabled={loading || success}
            required
          />
          <button
            type="submit"
            className={`w-full py-3 rounded-xl text-white font-bold text-lg shadow-lg transition-all ${
              loading || success 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
            disabled={loading || success}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Resetting...
              </span>
            ) : 'Reset Password'}
          </button>
        </form>
        {message && (
          <div className={`mt-4 text-center text-sm p-3 rounded-lg ${
            success 
              ? 'text-green-600 bg-green-50 border border-green-200' 
              : 'text-red-600 bg-red-50 border border-red-200'
          }`}>
            {message}
          </div>
        )}
        {success && (
          <button
            className="mt-4 px-6 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base shadow-lg transition-all"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </button>
        )}
      </div>
    </div>
  );
} 